import { createContext, useContext, useEffect, useState } from "react";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { getProductPriceForSize } from "../utils/pricing";

const ShopContext = createContext(null);

const SESSION_KEY = "oss-shop-session";
const NOTICE_KEY = "oss-shop-notice";
const CART_CLEARED_KEY = "oss-shop-cart-cleared";
const emptyShop = {
  cart: [],
  wishlist: []
};

function readSessionShop() {
  try {
    const value = sessionStorage.getItem(SESSION_KEY);
    return value ? { ...emptyShop, ...JSON.parse(value) } : emptyShop;
  } catch {
    return emptyShop;
  }
}

function writeSessionShop(shop) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(shop));
}

function writeSessionCartCleared() {
  sessionStorage.setItem(CART_CLEARED_KEY, "1");
}

function takeSessionCartCleared() {
  const cartWasCleared = sessionStorage.getItem(CART_CLEARED_KEY) === "1";

  if (cartWasCleared) {
    sessionStorage.removeItem(CART_CLEARED_KEY);
  }

  return cartWasCleared;
}

function productSnapshot(product, size = "") {
  const price = getProductPriceForSize(product, size);

  return {
    productId: product.id || product.productId,
    slug: product.slug,
    name: product.name,
    productCode: product.productCode || product.sku || "",
    price,
    basePrice: product.price,
    hasVariableSizePrices: product.hasVariableSizePrices === true,
    priceBySize: product.priceBySize || {},
    image: product.images?.[0] || product.image || product.thumbnailUrl || "",
    sectionId: product.sectionId,
    sectionIds: Array.isArray(product.sectionIds) ? product.sectionIds : [],
    category: product.category,
    sizes: Array.isArray(product.sizes) ? product.sizes : []
  };
}

function cartItemKey(productId, size) {
  return `${productId}:${size || "one-size"}`;
}

function mergeCart(localCart, savedCart) {
  const merged = new Map();

  [...savedCart, ...localCart].forEach((item) => {
    const key = item.key || cartItemKey(item.productId, item.size);
    const existing = merged.get(key);

    if (existing) {
      merged.set(key, {
        ...existing,
        quantity: existing.quantity + item.quantity
      });
      return;
    }

    merged.set(key, { ...item, key });
  });

  return Array.from(merged.values());
}

function mergeWishlist(localWishlist, savedWishlist) {
  const merged = new Map();

  [...savedWishlist, ...localWishlist].forEach((item) => {
    if (item.productId) {
      merged.set(item.productId, item);
    }
  });

  return Array.from(merged.values());
}

function buildCartAddition(shop, snapshot, key, size, quantity) {
  const existingItem = shop.cart.find((item) => item.key === key);

  if (existingItem) {
    return {
      ...shop,
      cart: shop.cart.map((item) =>
        item.key === key
          ? { ...item, quantity: item.quantity + quantity }
          : item
      )
    };
  }

  return {
    ...shop,
    cart: [
      ...shop.cart,
      {
        ...snapshot,
        key,
        size,
        quantity
      }
    ]
  };
}

export function ShopProvider({ children }) {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [shop, setShop] = useState(emptyShop);
  const [notice, setNotice] = useState("");
  const [loaded, setLoaded] = useState(false);

  const shouldUseCloud = Boolean(user && !isAdmin);

  useEffect(() => {
    const pendingNotice = sessionStorage.getItem(NOTICE_KEY);

    if (!pendingNotice) return;

    sessionStorage.removeItem(NOTICE_KEY);
    setNotice(pendingNotice);
  }, []);

  useEffect(() => {
    if (authLoading) return;

    async function loadShop() {
      const cartWasCleared = takeSessionCartCleared();
      const rawSessionShop = readSessionShop();
      const sessionShop = {
        ...rawSessionShop,
        cart: cartWasCleared ? [] : rawSessionShop.cart
      };

      if (!shouldUseCloud) {
        setShop(sessionShop);
        setLoaded(true);
        return;
      }

      try {
        const shopRef = doc(db, "carts", user.uid);
        const shopSnap = await getDoc(shopRef);
        const savedShop = shopSnap.exists()
          ? { ...emptyShop, ...shopSnap.data() }
          : emptyShop;

        const mergedShop = {
          cart: cartWasCleared
            ? []
            : mergeCart(sessionShop.cart, savedShop.cart || []),
          wishlist: mergeWishlist(sessionShop.wishlist, savedShop.wishlist || [])
        };

        setShop(mergedShop);
        setLoaded(true);
        sessionStorage.removeItem(SESSION_KEY);

        if (
          cartWasCleared ||
          sessionShop.cart.length > 0 ||
          sessionShop.wishlist.length > 0
        ) {
          await setDoc(
            shopRef,
            {
              ...mergedShop,
              updatedAt: serverTimestamp()
            },
            { merge: true }
          );
        }
      } catch (error) {
        console.error(error);
        setShop(sessionShop);
        setLoaded(true);
      }
    }

    setLoaded(false);
    loadShop();
  }, [authLoading, shouldUseCloud, user]);

  useEffect(() => {
    if (!loaded) return;

    if (!shouldUseCloud) {
      writeSessionShop(shop);
      return;
    }

    const saveTimer = window.setTimeout(() => {
      setDoc(
        doc(db, "carts", user.uid),
        {
          ...shop,
          updatedAt: serverTimestamp()
        }
      ).catch((error) => console.error(error));
    }, 300);

    return () => window.clearTimeout(saveTimer);
  }, [loaded, shop, shouldUseCloud, user]);

  useEffect(() => {
    if (!notice) return;

    const timer = window.setTimeout(() => {
      setNotice("");
    }, 2200);

    return () => window.clearTimeout(timer);
  }, [notice]);

  function persistShop(nextShop, options = {}) {
    if (!loaded && !options.force) return Promise.resolve();

    if (!shouldUseCloud) {
      writeSessionShop(nextShop);
      return Promise.resolve();
    }

    return setDoc(doc(db, "carts", user.uid), {
      ...nextShop,
      updatedAt: serverTimestamp()
    }).catch((error) => console.error(error));
  }

  function updateShop(updater) {
    setShop((current) => {
      const nextShop = updater(current);
      persistShop(nextShop);
      return nextShop;
    });
  }

  function addToCart(product, size = "", quantity = 1) {
    const snapshot = productSnapshot(product, size);
    const key = cartItemKey(snapshot.productId, size);
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    const currentShop = loaded ? shop : readSessionShop();
    const nextShop = buildCartAddition(
      currentShop,
      snapshot,
      key,
      size,
      safeQuantity
    );

    setShop(nextShop);
    if (!shouldUseCloud || !loaded) {
      writeSessionShop(nextShop);
    }
    showNotice(`${snapshot.name} προστέθηκε στο καλάθι.`);
    persistShop(nextShop);
  }

  function updateCartQuantity(key, quantity) {
    const safeQuantity = Math.max(1, Number(quantity) || 1);

    updateShop((current) => ({
      ...current,
      cart: current.cart.map((item) =>
        item.key === key ? { ...item, quantity: safeQuantity } : item
      )
    }));
  }

  function removeFromCart(key) {
    updateShop((current) => {
      const removedItem = current.cart.find((item) => item.key === key);
      const nextShop = {
        ...current,
        cart: current.cart.filter((item) => item.key !== key)
      };

      if (removedItem) {
        setNotice(`${removedItem.name} αφαιρέθηκε από το καλάθι.`);
      }

      return nextShop;
    });
  }

  function clearCart() {
    const nextShop = {
      ...shop,
      cart: []
    };

    setShop(nextShop);
    writeSessionCartCleared();
    writeSessionShop(nextShop);
    setNotice("Το καλάθι άδειασε.");
    return persistShop(nextShop, { force: true });
  }

  function toggleWishlist(product) {
    const snapshot = productSnapshot(product);

    updateShop((current) => {
      const exists = current.wishlist.some(
        (item) => item.productId === snapshot.productId
      );

      return {
        ...current,
        wishlist: exists
          ? current.wishlist.filter(
              (item) => item.productId !== snapshot.productId
            )
          : [...current.wishlist, snapshot]
      };
    });
  }

  function isWishlisted(productId) {
    return shop.wishlist.some((item) => item.productId === productId);
  }

  function showNotice(message, options = {}) {
    if (!message) return;

    if (options.persist) {
      sessionStorage.setItem(NOTICE_KEY, message);
    }

    setNotice(message);
  }

  const cartCount = shop.cart.reduce((total, item) => total + item.quantity, 0);
  const cartTotal = shop.cart.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  const value = {
    cart: shop.cart,
    wishlist: shop.wishlist,
    notice,
    loaded,
    cartCount,
    wishlistCount: shop.wishlist.length,
    cartTotal,
    showNotice,
    addToCart,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    toggleWishlist,
    isWishlisted,
    clearNotice: () => setNotice("")
  };

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop() {
  return useContext(ShopContext);
}
